import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Search, Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, MEGA_MENU } from "@/data/content";
import SearchModal from "@/components/SearchModal";

const PARTICIPATE = [
    { label: "Attend", to: "/register?type=attendee" },
    { label: "Exhibit", to: "/exhibition#book" },
    { label: "Partner", to: "/partner" },
];

const Wordmark = ({ compact }) => (
    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo" aria-label="Startup MahaBazaar 2027 — Home">
        <img src="/emblem.png" alt="Startup MahaBazaar emblem" className={`${compact ? "h-8" : "h-11"} w-auto transition-all duration-500`} />
        <span className="leading-none">
            <span className={`block text-[0.55rem] tracking-[0.5em] text-[var(--gold)] transition-all duration-500 ${compact ? "text-[0.48rem]" : ""}`}>STARTUP</span>
            <span className={`block font-display font-semibold gold-text tracking-wide transition-all duration-500 ${compact ? "text-base" : "text-lg"}`}>MAHABAZAAR</span>
            <span className="block text-[0.5rem] tracking-[0.62em] text-muted-luxe">2027</span>
        </span>
    </Link>
);

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [expOpen, setExpOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 40);
        fn();
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    useEffect(() => {
        setOpen(false);
        setExpOpen(false);
    }, [location]);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <>
            <header className="fixed top-0 inset-x-0 z-[80]" data-testid="main-nav">
                <div className={`overflow-hidden transition-all duration-500 ${scrolled ? "max-h-0" : "max-h-10"}`}>
                    <div className="bg-[var(--wine-deep)]/90 backdrop-blur border-b hairline">
                        <div className="container-x flex items-center justify-between py-1.5 text-[0.58rem] tracking-[0.32em] uppercase text-[var(--champagne)]/70">
                            <span>29–31 January 2027 · MSIT &amp; MSI, New Delhi</span>
                            <span className="hidden sm:block">Bazaar of Ideas, Innovation &amp; Impact</span>
                        </div>
                    </div>
                </div>
                <div className={`border-b hairline backdrop-blur-xl transition-all duration-500 ${scrolled ? "bg-[#160409]/92" : "bg-[#160409]/70"}`}>
                    <div className={`container-x flex items-center justify-between gap-6 transition-all duration-500 ${scrolled ? "py-2.5" : "py-4"}`}>
                        <Wordmark compact={scrolled} />

                        <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
                            {NAV_LINKS.map((l, i) =>
                                l.mega ? (
                                    <div key={l.label} className="relative group">
                                        <NavLink to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                                            className={({ isActive }) => `nav-link flex items-center gap-1 ${isActive ? "active" : ""}`}>
                                            {l.label}
                                            <ChevronDown size={10} className="opacity-60 group-hover:rotate-180 transition-transform duration-300" />
                                        </NavLink>
                                        <div className="invisible opacity-0 translate-y-3 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 absolute left-1/2 -translate-x-1/2 top-full pt-5 z-50">
                                            <div className="card-luxe w-[46rem] p-8 grid grid-cols-4 gap-8 shadow-2xl" data-testid="mega-menu">
                                                {MEGA_MENU.map((col) => (
                                                    <div key={col.head}>
                                                        <p className="text-[0.58rem] tracking-[0.36em] uppercase text-[var(--gold)] mb-4">{col.head}</p>
                                                        <ul className="space-y-3">
                                                            {col.items.map((item) => (
                                                                <li key={item.label}>
                                                                    <button onClick={() => navigate(item.to)}
                                                                        className="text-sm text-[var(--ivory)]/70 hover:text-[var(--champagne)] transition-colors text-left">
                                                                        {item.label}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <NavLink key={l.label} to={l.to} end={l.to === "/"} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={({ isActive }) => `nav-link ${isActive ? "active" : ""} ${i > 5 ? "hidden min-[2000px]:inline-block" : ""}`}>
                                        {l.label}
                                    </NavLink>
                                ),
                            )}
                        </nav>

                        <div className="flex items-center gap-3">
                            <button onClick={() => setSearchOpen(true)} data-testid="nav-search-btn" aria-label="Search"
                                className="w-9 h-9 hidden sm:flex items-center justify-center border hairline text-[var(--champagne)]/70 hover:text-[var(--champagne)] hover:border-[var(--line)] transition-colors">
                                <Search size={15} strokeWidth={1.5} />
                            </button>
                            <div className="relative group hidden min-[2000px]:block">
                                <button data-testid="nav-participate-btn" className="btn btn-dark !py-2.5 !px-5">
                                    Participate <ChevronDown size={11} />
                                </button>
                                <div className="invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 absolute right-0 top-full pt-3 z-50">
                                    <div className="card-luxe w-52 p-2 shadow-2xl">
                                        {PARTICIPATE.map((p) => (
                                            <button key={p.label} onClick={() => navigate(p.to)} data-testid={`nav-${p.label.toLowerCase()}`}
                                                className="w-full text-left px-4 py-3 text-xs tracking-[0.24em] uppercase text-[var(--ivory)]/75 hover:text-[var(--champagne)] hover:bg-[rgba(201,162,75,0.07)] transition-colors">
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <Link to="/register" data-testid="nav-register-btn" className="btn btn-gold !py-2.5 !px-6 hidden sm:inline-flex">
                                Register
                            </Link>
                            <button onClick={() => setOpen(true)} data-testid="nav-menu-btn" aria-label="Open full menu"
                                className="min-[2000px]:hidden h-10 px-3 flex items-center gap-2 border hairline text-[var(--champagne)] hover:border-[var(--line)] transition-colors">
                                <Menu size={17} strokeWidth={1.5} />
                                <span className="hidden md:inline text-[0.58rem] tracking-[0.3em] uppercase">Menu</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {open && (
                <div className="fixed inset-0 z-[90] bg-[#100206]/98 backdrop-blur-xl overflow-y-auto" data-testid="mobile-menu">
                    <div className="container-x py-6 flex items-center justify-between">
                        <Wordmark compact />
                        <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"
                            className="w-10 h-10 flex items-center justify-center border hairline text-[var(--champagne)]">
                            <X size={18} strokeWidth={1.5} />
                        </button>
                    </div>
                    <nav className="container-x pb-32" aria-label="Mobile">
                        <button onClick={() => { setSearchOpen(true); setOpen(false); }} data-testid="mobile-search-btn"
                            className="w-full flex items-center gap-3 py-4 border-b hairline text-left text-[var(--champagne)]/80 text-sm tracking-[0.3em] uppercase">
                            <Search size={16} strokeWidth={1.5} /> Search the Bazaar
                        </button>
                        {NAV_LINKS.map((l, i) => (
                            <div key={l.label}>
                                {l.mega ? (
                                    <>
                                        <button onClick={() => setExpOpen(!expOpen)} data-testid="mobile-experience-toggle"
                                            className="w-full flex items-center justify-between py-5 border-b hairline font-display text-2xl text-[var(--ivory)]">
                                            Experience
                                            <ChevronDown size={18} className={`transition-transform duration-300 ${expOpen ? "rotate-180" : ""}`} />
                                        </button>
                                        {expOpen && (
                                            <div className="py-4 border-b hairline grid grid-cols-2 gap-x-6 gap-y-3">
                                                <button onClick={() => navigate("/experience")} className="text-left text-sm text-[var(--gold)] tracking-[0.2em] uppercase">Overview</button>
                                                {MEGA_MENU.flatMap((c) => c.items).map((item) => (
                                                    <button key={item.label} onClick={() => navigate(item.to)}
                                                        className="text-left text-sm text-[var(--ivory)]/70">
                                                        {item.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <button key={l.label} onClick={() => navigate(l.to)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="w-full text-left py-5 border-b hairline font-display text-2xl text-[var(--ivory)] hover:text-[var(--champagne)] transition-colors"
                                        style={{ animationDelay: `${i * 40}ms` }}>
                                        {l.label}
                                    </button>
                                )}
                            </div>
                        ))}
                        <div className="grid grid-cols-2 gap-3 mt-8">
                            {PARTICIPATE.map((p) => (
                                <button key={p.label} onClick={() => navigate(p.to)} className="btn btn-outline !px-3" data-testid={`mobile-${p.label.toLowerCase()}-btn`}>
                                    {p.label}
                                </button>
                            ))}
                            <button onClick={() => navigate("/register")} className="btn btn-gold !px-3" data-testid="mobile-register-btn">
                                Register
                            </button>
                            <button onClick={() => navigate("/contact")} className="btn btn-dark !px-3" data-testid="mobile-contact-btn">
                                Contact
                            </button>
                        </div>
                    </nav>
                </div>
            )}

            <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
        </>
    );
}
